<template>
  <GcBaseLayout
    :title="'Sizer Console'"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :baseView="baseView"
    :key="pageKey"
    :backButtonEnable="true"
    :backButtonFunction="backButtonFunction"
    :formsConfig="formsConfig"
  >
    <template #headerContent>
      <GcHeaderContentDynamic
        :title="'Sizer Console'"
        :createModeIsEditMode="true"
        :baseView="baseView"
        :primaryLabel="'Sizer Console'"
        :secondaryLabel="undefined"
      />
    </template>
    <div class="split-view-template css-v2 split-view sizer-console">
      <GcSplitView
        :showLeftContainer="true"
        :showRightContainer="showRightPanel"
      >
        <template #leftContent>
          <div class="css-v2 split-view-list">
            <GcDataGrid
              :dataSourceConfig="leftGridConfig.gridData.gridOptions.dataSource"
              :columns="leftGridConfig.gridData.columns"
              :gridToolbar="leftGridConfig.gridData.toolbar"
              :padColumn="true"
              :id="'sizer-console-left-grid'"
              :rowClick="onLeftRowClick"
              :exportFileName="leftGridConfig.exportFileName"
              :heightPadding="20"
              :paging="false"
              ref="leftGrid"
              v-if="loaded"
            />
          </div>
        </template>

        <template #rightContent>
          <div class="right-panel-container" v-if="showRightPanel">
            <GcFormHeaderToolbar
              :toolbarOptions="rightToolbarOptions"
              :dataModel="selectedRecord"
              :baseView="baseView"
              :backRoute="leftGridConfig.pageRoute"
              :itemSaveCallBack="onSaveClicked"
            />

            <div class="selected-record-info">
              <GcForm
                ref="headerForm"
                :formOptions="{ gcLabelLocation: 'left' }"
                :formItems="headerFormItems"
                :isNormal="true"
                :data-model="selectedRecord"
                :formMode="baseView.mode"
                :formLocation="'normal'"
              />
            </div>

            <div class="view-card responsive-paddings split-view-right-grid">
              <!--
                Deliberately a raw devextreme-vue DxDataGrid, not GcDataGrid -
                developer direction (Task 76661): GcDataGrid's own top-level
                column pipeline (gc-data-grid-v3.vue's `gridColumns` computed)
                filters out any column with no dataField before its nested-
                column handling ever runs, which would silently strip every
                one of this grid's 4 bands (a band itself has no dataField -
                only its children do) - confirmed by reading that code this
                task, not assumed. Raw DxDataGrid supports DevExtreme's real
                banded-column syntax natively, matching technical-design.md
                §6's explicit "16 columns under 4 banded groups" spec exactly,
                with no shared-component risk. Same reasoning already applied
                by the sibling scheme-prices.vue/grower-pool-splits.vue for a
                different limitation (GcDataGrid v1 hardcodes allow-updating +
                mode="cell" with no allowAdding/allowDeleting support) - this
                screen simply hits a second, independent reason to do the same
                thing.

                column-auto-width deliberately NOT set (06Sep2026 correction -
                developer-reported: "editable grid width too much as expected
                due to the number of editable cells... not giving the
                scrolling bar"). Every sibling 2116 grid (scheme-prices.vue/
                inshell-product.vue/grower-pool-splits.vue/grower-syndicate-
                splits.vue) sets column-auto-width: true, but each of those
                has only 4-5 columns that already fit the panel - this grid
                has 16 across 4 bands, deliberately widened past the visible
                panel by technical-design.md §6/prototype.html's own exact
                pixel widths (ColumnWidth.*). column-auto-width: true tells
                DevExtreme to compress every column to fit the container's
                own width instead of respecting each column's explicit
                width/minWidth - with this many columns that clips/squeezes
                cells rather than ever producing the horizontal scrollbar a
                genuinely-wider-than-the-panel grid needs. Leaving it unset
                (DevExtreme's own default, false) restores that - explicit
                DxScrolling below makes the resulting horizontal scrollbar
                always visible rather than only-on-hover, so a user isn't
                left guessing that the remaining columns exist at all.
              -->
              <DxDataGrid
                :data-source="cellRows"
                key-expr="RecordId"
                :show-borders="false"
                :repaint-changes-only="true"
                :column-auto-width="false"
                no-data-text="No Cells configured for this Console. Cells cannot be added or removed from this screen."
                :columnChooser="{ enabled: false }"
                :height="getHeight"
                @content-ready="onGridContentReady"
                @key-down="onGridKeyDown"
                ref="rightGrid"
                :columns="cellGridColumns"
                v-if="rightGridLoaded"
              >
                <DxScrolling mode="standard" :show-scrollbar="'always'" />
                <DxLoadPanel :show-indicator="false" :enabled="false" />
                <DxPaging :page-size="100" :enabled="false" />
                <DxPager :visible="false" />
                <!-- Update-only (BR-07) - every Cell row visible is editable
                     in Edit mode, but the row count never changes: no
                     allow-adding, no allow-deleting, hardcoded (not
                     permission-gated, §6's own deliberate deviation from
                     intake-variety-size.vue's allowDeleting-tied-to-permission
                     convention - no permission level should ever be able to
                     add/remove a Cell row here, since the reused legacy write
                     procedure itself has no INSERT/DELETE for
                     SimarlooConsoleCells at all).

                     allow-updating bound to isEditMode, not hardcoded true
                     (06Sep2026 correction) - a Review-only permission user
                     must see and be able to open a Console without ever
                     being able to edit its Cells; the grid must lock in
                     Review the same way the header fields above do. -->
                <DxEditing
                  mode="cell"
                  :allow-updating="isEditMode"
                  :allow-adding="false"
                  :allow-deleting="false"
                  :use-icons="true"
                  :select-text-on-edit-start="true"
                />
              </DxDataGrid>

              <div class="split-grid-footer" v-if="rightGridLoaded">
                <span>Cell Count: {{ cellRows.length }}</span>
              </div>
            </div>
          </div>
        </template>
      </GcSplitView>
    </div>
  </GcBaseLayout>
</template>

<script>
/*
Description
    sizer-console.vue - Settings > Intake > Sizer Console (Project 2116,
    Almondco Intake Masters Migration). List + filter panel (#76623) - the
    List (Console Name/Console Type/Cell Count/Status) and its filter panel
    (Console Type tag box, Status), plus enough route/identity-resolution
    scaffolding for a List row click to open the detail panel directly.

    Row click always opens the detail panel in Review first
    (rowSelect.mode: "review", unconditionally) - the same standard
    List+Detail convention every sibling screen uses. Edit is reached only
    via the toolbar's Edit pencil, gated by the same `update` permission
    check as Save (canEditConsole - see rightToolbarOptions' own comment for
    how one flag gates both). 15Sep2026 correction (see History): an earlier
    revision of this screen (07Sep2026) instead resolved Review vs Edit from
    permission at the moment of the row click - a canEditConsole user
    routing straight to Edit, everyone else to Review - because
    technical-design.md §6/AD-03 stated at the time that this screen should
    open "directly in an editable state, no Review/Edit toggle" at all. That
    statement was itself a misreading of the actual project direction,
    confirmed directly with the PO (ADO #77470): the only intended deviation
    from the standard List+Detail pattern is the absence of Add New/create
    (BR-06/BR-07) - Review/Edit was always meant to work the normal way, the
    same as intake-variety-size.vue and every other sibling. technical-
    design.md §6/AD-03 has been corrected to match; this file reverts to the
    standard, unconditional Review-first row click as a result - a
    Review-only-permission user simply never sees an Edit pencil, since
    rightToolbarOptions.save (canEditConsole) is what gates it, exactly like
    every sibling screen.

    #76661 (Story 2 - detail panel, header fields + banded Cell grid): the
    real detail panel. Header fields (Console Name, Console Type, Commission
    Date, Retire Date), the Active toggle (inverted-polarity Inactive) and
    Audit popover in the panel toolbar, and the 16-column Cell grid under 4
    banded groups (Cell / Serial RS-232 / Scale Head TCP-IP / Status), per
    technical-design.md §6 and prototype.html.

    STRUCTURAL CHANGE this task, developer-directed: earlier tasks this
    project (#76623/#76628) built this screen on SplitViewGridTemplate
    (src/templates/split-view-grid.vue), per AD-03's own recorded decision.
    That template's right panel is hardcoded to render its embedded grid via
    GcDataGrid, with no slot override - and GcDataGrid's own column pipeline
    cannot carry the 16-column Cell grid's 4 required bands (confirmed by
    reading gc-data-grid-v3.vue's gridColumns computed this task: its
    top-level `.filter(x => x.dataField != null)` runs BEFORE the nested-
    column handling that already exists further down in parseColumn, so a
    band - which has no dataField of its own, only its children do - would
    be silently stripped before ever reaching that nested-column support).
    Per explicit developer direction, this task abandons SplitViewGridTemplate
    entirely in favour of a manually-composed GcBaseLayout + GcSplitView +
    GcFormHeaderToolbar + GcForm + a raw devextreme-vue DxDataGrid - the same
    real, already-shipped structural pattern this project's own
    scheme-prices.vue uses (for a different but related reason: GcDataGrid v1
    also cannot express PriceType-driven per-row column enable/disable).
    leftGridConfig itself is carried forward unchanged in shape (dataSource/
    columns/toolbar/gridOptions.rowSelect) - only how it is hosted changed
    (a plain GcDataGrid call here instead of SplitViewGridTemplate's own),
    so #76628's own List-config regression-guard tests stay valid; only the
    former "rightGridConfig stub" tests needed rewriting, since that whole
    config shape no longer exists (see sizer-console.spec.js's own History).

    Single `selectedRecord` object serves all three roles at once (GcForm's
    header fields, GcFormHeaderToolbar's dataModel for the Audit popover +
    Active toggle, and the Cell grid's Console-context) - unlike
    scheme-prices.vue's own split between a narrow identity-only
    selectedRecord and a separate audit-bearing toolbarRecord, which exists
    there only because Scheme Price's header fields are read-only identity,
    distinct from the full summary row. Sizer Console's header fields ARE
    the editable ones, so one shared object is simpler and correct here.

    Audit popover gap found and fixed this task: AlmondcoSizerConsoleSummary
    (the view/model this screen's header reads, #76619) never carried
    CreatedBy/DateCreated/ChangedBy/DateChanged - it predates those columns
    (#76647). Per technical-design.md §9 Story 2's own "no second header
    fetch required" statement, the fix is adding those 4 columns to the
    existing view/model (matching the AlmondcoSchemePriceSummary/
    AlmondcoGrowerPoolSplitSummary precedent), not a second fetch against the
    raw api/SimarlooConsoles endpoint - see that view/model's own History.

    Active toggle: bound to InactiveBoolean (computed on load from
    Inactive === "N", same convention as intake-variety-size.vue's own
    Inactive switch), writing back Inactive ("N"/"Y") on change - the real
    field the Save payload submits. Left interactive regardless of Review/
    Edit mode (readOnly not conditioned on baseView.mode), matching
    grower-regions.vue's own toolbarFormItems Inactive switch precedent
    exactly - harmless in Review, since GcFormHeaderToolbar never renders a
    Save button there for the change to reach (only Edit shows), so there is
    no path for a Review-mode toggle to actually persist anything.

    Review/Edit field-level gating (06Sep2026 correction, refined 07Sep2026 -
    see History): a first pass landed every row click on Review
    (rowSelect.mode: "review", unconditionally) with Edit reached only via a
    manual toolbar button - but nothing was actually locking the header
    fields or Cell grid down in that Review state, so Review looked
    identical to Edit. Fixed by conditioning header field
    editorOptions.disabled and the Cell grid's DxEditing allow-updating on
    isEditMode (`baseView.mode === "edit" && canEditConsole`) - the same
    established pattern already used by customer-agent.vue/grower-tab.vue/
    product-detail.vue and others (`disabled: this.baseView.mode ==
    "review"`), not a generic mode-gating mechanism gc-form.vue provides on
    its own (confirmed by reading it this session - gc-form.vue only ever
    reads baseView.mode to toggle readOnly on a field whose own
    `allowUpdate` is already `false`; a normal `allowUpdate: true` field like
    every one of this screen's header fields is never auto-locked by mode,
    each screen must gate it explicitly). This gating itself is unaffected
    by the 15Sep2026 correction above (which only reverted how Review vs
    Edit gets decided, back to the standard unconditional row click) - the
    isEditMode formula stays `baseView.mode === "edit" && canEditConsole`.

    Cell grid height: setRightGridHeight/onGridContentReady mirror
    scheme-prices.vue's own pattern, but with one deliberate correction - that
    screen's setRightGridHeight queries ".filter-split-container", which does
    not exist anywhere in gc-split-view.vue's real template (confirmed by
    reading it this task) and so silently no-ops every time; its own comment
    even claims ".split-container-child.right-container" is the fix already
    applied, but the code was never actually updated to match. This is a real,
    live bug in an already-shipped sibling screen - out of scope to fix here
    (a different feature/file), flagged to the developer rather than silently
    copied forward. This file uses the actually-correct selector,
    ".split-container-child.right-container", verified directly against
    gc-split-view.vue's real markup.

History
    03Sep2026 (SD)
        - #76623: 2116 - UI - Sizer Console list screen, search box, and
          filter panel (Story 1 only). Wires leftGridConfig against
          api/AlmondcoSizerConsoleSummary (§5): Console Name (identity column,
          search target, fixed left/non-hideable), Console Type (filter panel,
          fixed 3-value domain - new ConsoleType ScreenField registered,
          gcorp-api-netcore/Graycorp.Gcorp.Database.Financials/Scripts/
          Post-Deployment/ScreenField/ConsoleType.sql), Cell Count (plain
          numeric column, server-computed - never client-aggregated, §5/§8),
          Status (Active/Inactive filter chip - reuses the existing generic
          "Status" ScreenField, no new registration needed). No + ADD NEW
          anywhere (BR-06/FLAG-02 resolved - Consoles are never created
          through this screen). Filter panel closed by default (Grower
          Regions precedent, not the season/composite-key siblings' override -
          matches technical-design.md §6).
        - rowSelect.mode set to "review" (§6/AD-03) - a List row click opens
          the detail panel in Review first, matching every other List+Detail
          screen's own convention; Edit is reached only via the toolbar's
          Edit button.
        - Navigation entry (settings-list.vue) and routes (router/settings/
          intake.js) added early this task too, ahead of this Story's own
          "Navigation entry and routes" task, at developer request to verify
          the List screen directly - /sizer-console, /sizer-console/
          review/:id and /sizer-console/edit/:id registered (no /create).
    06Sep2026 (SD)
        - #76661 follow-up (developer correction, "go and recheck... it
          should go to review... one of the reasons is a user can only have
          review permission, not the create/edit"): found this file's own
          comments/History and sizer-console.spec.js's TC-010 both still
          claimed rowSelect.mode was "edit" (straight-to-edit, no Review
          step), even though the code itself (rowSelect.mode above, and the
          /review/:id route already registered in router/settings/intake.js)
          had actually been "review" all along - a stale-documentation
          mismatch, not a routing bug. The real, functional gap: nothing
          actually locked the header fields or the Cell grid down while in
          Review - headerFormItems' editorOptions had no disabled/readOnly
          condition at all, and the Cell grid's DxEditing allow-updating was
          hardcoded `true` - so Review rendered identical to Edit regardless
          of permission. Fixed both to gate on `baseView.mode === "edit"`,
          the same established convention already used by customer-agent.vue/
          grower-tab.vue/product-detail.vue (confirmed this session, not
          assumed) - not a mode-gating mechanism gc-form.vue provides
          automatically (also confirmed by reading it: it only reads
          baseView.mode for a field whose own allowUpdate is already false).
          Corrected the stale comments/History here and in router/settings/
          intake.js, and TC-010's title/assertions in sizer-console.spec.js,
          to match reality instead of leaving the code right and the
          documentation wrong.
    03Sep2026 (SD)
        - #76628: 2116 - Developer Testing - List view, search, filtering.
          Added tests/unit/views/settings/intake/sizer-console.spec.js (11
          tests) covering everything client-side-verifiable in test-cases.md
          Story 1. Real, previously-undiscovered infrastructure gap found and
          fixed along the way: vitest.config.js had no `.vue` in
          `resolve.extensions` - fixed there; confirmed against the full
          suite (151/151 passing, 8 files) with no regressions.
    06Sep2026 (SD)
        - #76661: 2116 - UI - Sizer Console detail panel - header fields and
          banded Cell grid. See the Description block above for the full
          structural rewrite (SplitViewGridTemplate -> manually-composed
          GcBaseLayout/GcSplitView/raw DxDataGrid) and the two gaps found and
          fixed along the way (Audit popover's missing summary columns, the
          sibling scheme-prices.vue grid-height selector bug flagged but not
          fixed here).
        - Follow-up same day (developer-reported: "editable grid width too
          much as expected due to the number of editable cells... not
          giving the scrolling bar"): removed column-auto-width (inherited
          unchanged from every sibling 2116 grid, none of which have enough
          columns for it to matter) and added an explicit always-visible
          DxScrolling - see the template's own comment for the full
          reasoning.
        - Follow-up same day (developer-reported: required-field validation
          errors were coming back from the API but not shown properly):
          onSaveClicked's catch block only ever handled a BadRequest array
          of ValidationResult-shaped objects (the business validator's own
          shape) or a plain string - it never handled ASP.NET Core
          [ApiController]'s own automatic ModelState validation response
          (RFC 9110 problem+json, `errors: { "Cells[0].Parity": [...] }`),
          which fires from SaveModels/SimarlooConsole.cs's [Required]/
          [MaxLength] annotations before the request ever reaches the
          controller action - every sibling 2116 screen's own Save error
          handling has this exact same gap (checked this session), but
          fixing it there is out of scope for this task. Now parsed and
          rendered as an actual list (gc-popup-alert.vue's message is
          v-html-bound already), each "Cells[N]." field prefixed with its
          1-based row number so multiple failing rows stay distinguishable.
        - Follow-up same day (developer-requested tabbing sequence, matching
          scheme-prices.vue's own earlier tabbing work): Console Name
          auto-focuses on entering Edit mode; Retire Date (the last header
          field) forward-Tabs into the Cell grid's row 0/column 0 instead of
          native document tab order; the grid's own row 0/column 0
          Shift+Tabs back to Retire Date. See headerFormItems' own comment
          for the full design - no per-row skip logic is needed here (unlike
          scheme-prices.vue's own onGridKeyDown), since every Cell grid
          column in this screen is always editable in Edit mode.
    07Sep2026 (SD)
        - #76666 follow-up (developer-flagged discrepancy, then developer-
          directed resolution): test-cases.md TC-010/TC-027/TC-058 and
          technical-design.md §6/AD-03/feature-vision.md FLAG-01 all record,
          as a deliberate reviewed decision, that this screen opens directly
          editable with no Review/Edit toggle and no Edit icon - directly
          contradicting the manual-Edit-button design the 06Sep2026 entries
          above built, which the developer had separately, explicitly
          directed ("we dont go to edit directly... user can only have
          review permission"). Flagged rather than silently building either
          side. Resolved by the developer as: route permission-based instead
          of via a manual step - see the Description block's own top section
          and canEditConsole/isEditMode/rightToolbarOptions.save/
          onLeftRowClick's own comments for the full mechanism. This
          reconciles both documents at once (see Description) rather than
          picking one over the other; test-cases.md TC-010/TC-058/TC-064
          should be updated to describe this resolved behaviour precisely -
          not yet done as part of this task, offered to the developer
          separately.
    07Sep2026 (SD)
        - Bug #77465: Console Name's maxLength lowered from 255 to 50 -
          255 matched ConsoleName's own real NVARCHAR(255) column, but
          BR-08's rename cascade also writes into
          NextInstanceTransactionNo.GcorpInstance (NVARCHAR(50)) for any
          Console with a matching TransactionType IN ('NewBins','NewBinsA')
          row - confirmed against real DDL. See headerFormItems' own comment
          and investigation.md (Almondco - 2116 - Sizer Console Master) for
          the full root-cause writeup, including the deferred conditional
          alternative and the paired shim-SP fix (AlmondcoSizerConsoleWebAppWrite.sql)
          so a DB-level Save failure never surfaces a raw SQL error again.
    07Sep2026 (SD)
        - Bug #77467: QA-clarified (developer confirmed: "not a casing issue,
          the message just isn't user-friendly"): added friendlyFieldLabel
          and consolidated buildSaveErrorMessage's ModelState-shape branch
          into per-row "Row N is missing: A, B, C." wording, so the message
          no longer echoes raw ASP.NET Core sentences/property names.
          Follow-up (developer-clarified: "don't we know what that error
          comes from... that should be the error message"): added a further
          shape for a ProblemDetails object with no errors dictionary
          (title/detail only) - this is exactly what
          GlobalExceptionHandlingMiddleware.cs already returns for any
          unhandled server exception during Save, with a real reason and a
          Trace ID, previously discarded the same way the above gap was.
          The true last-resort fallback (nothing recognisable at all) also
          got a next-step sentence instead of a dead end.
          CORRECTION (same day): a brief intermediate version of this fix
          changed the ModelState-shape branch to check data.Errors (capital
          E) on the theory that Program.cs's PropertyNamingPolicy = null
          means real responses serialize PascalCase. The developer confirmed
          empirically against a real response that this is wrong -
          data.errors (lowercase) is correct, because ASP.NET Core's built-in
          ValidationProblemDetails/ProblemDetails classes carry their own
          hardcoded [JsonPropertyName] attributes (RFC 9110, lowercase),
          which always win over an app-wide PropertyNamingPolicy regardless
          of Program.cs. Reverted to data.errors/data.detail/data.title
          throughout. See all three methods' own comments and
          investigation.md (Almondco - 2116 - Sizer Console Master) for the
          full write-up, including this correction.
    15Sep2026 (SD)
        - Bug #77470 root cause resolved: technical-design.md §6/AD-03's
          "no Review/Edit toggle at all" spec (which the 06/07Sep2026 entries
          above built toward) was a misreading of the project's actual
          direction, confirmed directly with the PO - the only intended
          deviation from the standard List+Detail pattern was ever Add
          New/create (BR-06/BR-07), not Review/Edit. technical-design.md
          §3/§6/§9/AD-03/§11 corrected accordingly.
        - Reverted this file to the standard, unconditional Review-first row
          click (rowSelect.mode: "review", onLeftRowClick) and removed the
          07Sep2026 canEditConsole-based row-click routing entirely - see
          the Description block's top section and onLeftRowClick/
          canEditConsole/rightToolbarOptions' own comments for the corrected
          mechanism. A Review-only-permission user is still kept off an
          editable form, the same way every sibling screen already handles
          it: rightToolbarOptions.save (canEditConsole) gates whether the
          Edit pencil renders at all in Review - no bespoke routing needed.
        - Removed `showInAuditInEdit: true` (added in a since-superseded fix,
          commit 9d0a02044/#77489, to show Audit in Edit mode) - now that
          Edit is reached only via the standard pencil step, Audit is a
          Review-mode-only affordance again, matching every sibling screen
          and closing the other half of Bug #77470 (Audit only in Review,
          Save only in Edit is the correct, standard shape - not a defect).
        - test-cases.md TC-010/TC-027/TC-058/TC-064/TC-066 describe the
          superseded behaviour and need updating to match; not done as part
          of this task, offered to the developer separately.
    15Sep2026 (SD)
        - Bug reported (developer, real-data screenshot): Save blocked on
          Console ALM-1121-DT01 with "Row 2/3 is missing: Port Name, Parity,
          Stop Bits" for its Inshell Bag/Inshell Bin Tip Cells. Root cause:
          PortName/Parity/StopBits/DataBits/BaudRate were required
          unconditionally, but real data (this project's own prototype.html,
          PO-supplied 2026-08-25) already showed these two Cell Types are
          non-communicating destination Cells with no port assignment at
          all - the distinction was captured in the prototype's mock data
          but never carried into the actual required-field rule. Fixed here
          (validateBeforeSave, NON_COMMUNICATING_CELL_TYPES) and server-side
          (SaveModels/SimarlooConsole.cs, SimarlooConsoleBusinessRulesValidator.cs)
          - these 5 fields are now required only for a communicating Cell
          (blank CellType, Sizer, or Inshell Bin). DataBits/BaudRate lookups
          extended to include the real 0 sentinel these two Cell Types store,
          matching the existing IPAddress/PortNumber sentinel-default
          treatment (§8 Default Value Contract) rather than inventing a new
          convention. technical-design.md/user-stories.md/test-cases.md
          updated to record this as a new, corrected business rule.

(c) Copyright Graycorp Pty Ltd (ABN 12 082 807 761). All Rights Reserved.
*/
import { FeatureStorage } from "@/_services/storage";
import RouteMixin from "@/_mixins/route.mixin";
import FormMixin from "@/_mixins/form.mixin";
import ColumnWidth from "../../../_helpers/data-grid/ColumnWidth";
import { AlmondcoSizerConsoleService } from "@/_services/api/settings/purchasing/almondco-sizer-console.service";
import { DxDataGrid } from "devextreme-vue/data-grid";

export default {
  mixins: [RouteMixin, FormMixin],
  components: { DxDataGrid },
  data() {
    return {
      ColumnWidth,
      loaded: false,
      rightGridHeight: "auto",

      // The selected List row's full header record - resolved from the
      // route's RecordId via a lookup against AlmondcoSizerConsoleSummary
      // (see loadSelectedRecord). Serves as GcForm's data-model,
      // GcFormHeaderToolbar's dataModel (Audit popover + Active toggle), and
      // the Cell grid's Console-context, all at once - see this file's own
      // Description for why this differs from scheme-prices.vue's split
      // selectedRecord/toolbarRecord.
      selectedRecord: {},

      // Local, mutable array bound directly to DxDataGrid's data-source -
      // every field editable in place via DxEditing's own allow-updating,
      // but allow-adding/allow-deleting are both hardcoded false (BR-07) -
      // this array's own row count never changes via this screen.
      cellRows: [],

      leftGridConfig: {
        title: "Sizer Console",
        pageRoute: "/sizer-console",
        recordEndPoint: "AlmondcoSizerConsoleSummary",
        exportFileName: "Sizer Console List",

        gridData: {
          gridOptions: {
            dataSource: {
              endpoint: "AlmondcoSizerConsoleSummary",
              filter: "",
              serverPaging: true,
              allowGrouping: false,
              sort: [{ selector: "ConsoleName", desc: false }],
            },
            // 15Sep2026 correction (see History) - reverted to the standard,
            // static "review" mode every other List+Detail screen uses
            // (matches intake-variety-size.vue's own rowSelect.mode: "review"
            // exactly). The 07Sep2026 canEditConsole-based per-click override
            // this replaced is gone along with it - see onLeftRowClick below.
            rowSelect: {
              view: this,
              page: "sizer-console",
              mode: "review",
              idProperty: "RecordId",
              idMode: "Guid",
            },
          },

          toolbar: {
            search: true,
            columnChooser: true,
            export: false,
            openFilter: true,
            expand: false,
            // No + ADD NEW - Consoles are never created through this screen
            // (BR-06, FLAG-02 resolved, technical-design.md §6).
            customButtons: [],
          },

          columns: [
            {
              screenField: "ConsoleName",
              dataField: "ConsoleName",
              label: "Console Name",
              allowHiding: false,
              sortIndex: 0,
              sortOrder: "asc",
              width: 170,
              forceWidth: true,
            },
            {
              screenField: "ConsoleType",
              dataField: "ConsoleType",
              label: "Console Type",
              filterIndex: 0,
            },
            {
              screenField: "CellCount",
              dataField: "CellCount",
              label: "Cell Count",
              alignment: "right",
              allowHiding: false,
              width: 100,
              forceWidth: true,
            },
            {
              screenField: "Status",
              width: "auto",
              filterIndex: 1,
            },
          ],

          valueChangedEventListners: [],
        },
      },
      rightGridLoaded: false,
    };
  },

  computed: {
    showRightPanel() {
      return (
        this.baseView.id !== null &&
        this.baseView.id !== undefined &&
        this.baseView.id !== ""
      );
    },

    getHeight() {
      return this.rightGridHeight;
    },

    // Single source of truth for "can this user actually edit a Console".
    // 15Sep2026 correction (see History): no longer drives row-click routing
    // (every row click opens Review unconditionally now, per the standard
    // pattern - onLeftRowClick). Still drives the two things it should:
    // whether the header fields/Cell grid are locked (isEditMode below), and
    // whether GcFormHeaderToolbar renders Save/Edit at all
    // (rightToolbarOptions.save - see that computed's own comment for how a
    // single flag gates both).
    canEditConsole() {
      return FeatureStorage.checkPermission("AlmondcoSizerConsole", "create");
    },

    // True only in Edit mode AND with edit permission - the permission half
    // is defence-in-depth against a direct/bookmarked /edit/:id visit by a
    // Review-only user (GcFormHeaderToolbar's own Edit-button visibility
    // already keeps them from reaching Edit via the UI, but a hand-typed URL
    // bypasses that). There is no create mode on this screen (BR-06/BR-07),
    // so this is simply `mode === "edit" && canEditConsole`, not a broader
    // isEditing(mode) ternary like scheme-prices.vue's own (which also has
    // to account for create).
    isEditMode() {
      return this.baseView.mode === "edit" && this.canEditConsole;
    },

    // §6: Update-only save gate - the create-vs-update ternary the sibling
    // split screens need doesn't apply here (there is no create mode at
    // all, BR-06/BR-07), so this is a single unconditional `update`
    // permission check. GcFormHeaderToolbar reads this same boolean for two
    // things at once (gc-form-header-toolbar.vue's own toolbar.save/
    // toolbar.edit template conditions, confirmed by reading it): it shows
    // Save when mode === "edit", and shows the Edit pencil when
    // mode === "review" - so a Review-only-permission user (save: false)
    // never sees an Edit pencil at all, the same way every sibling screen
    // already handles this, with no bespoke routing logic needed.
    rightToolbarOptions() {
      return {
        closeSplit: true,
        save: this.canEditConsole,
        audit: true,
        auditItems: [
          {
            itemType: "group",
            caption: "",
            items: [
              {
                dataField: "FormattedDateCreated",
                label: { text: "Created By", location: "left" },
                editorType: "dxTextBox",
                editorOptions: { disabled: true },
              },
              {
                dataField: "FormattedDateChanged",
                label: { text: "Changed By", location: "left" },
                editorType: "dxTextBox",
                editorOptions: { disabled: true },
              },
            ],
          },
        ],
        toolbarAfterFormItems: [
          {
            screenField: "Inactive",
            dataField: "InactiveBoolean",
            editorType: "dxSwitch",
            editorOptions: {
              // Named method reference, not an inline arrow function - a
              // direct `this.selectedRecord.x = ...` assignment written
              // lexically inside a computed property's own body trips
              // vue/no-side-effects-in-computed-properties, even though this
              // only ever runs from a user interaction, never from the
              // computed getter re-evaluating. Sibling screens
              // (intake-variety-size.vue) avoid the same lint rule by
              // mutating through a $refs indirection instead; a method
              // reference is the more direct fix.
              onValueChanged: this.onActiveToggleChanged,
            },
            cssClass: "toolbar-customer-label",
          },
        ],
        toolbarFormLocation: "after",
        editCallBack: this.editCallBack,
      };
    },

    // §6 header fields: Console Name (dxTextBox, maxLength 50, required) -
    // 07Sep2026 (SD), Bug #77465: was 255, matching ConsoleName's own real
    // NVARCHAR(255) column, but BR-08's rename cascade also writes the new
    // name into NextInstanceTransactionNo.GcorpInstance (for any Console
    // with a matching TransactionType IN ('NewBins','NewBinsA') row), and
    // that column is NVARCHAR(50) - confirmed against real DDL. 255 was
    // false confidence: up to 205 characters beyond the real constraint
    // passed validation, then failed at Save with a raw SQL truncation
    // error (Error 2628) for any Console that happened to have such a row -
    // data-dependent, so easy to miss in testing. Lowered to 50 uniformly
    // (not just for Consoles with a matching row today) since such a row
    // could be added later by an unrelated legacy process independent of
    // this screen - see investigation.md's "Deferred fix" for the
    // conditional alternative considered and rejected. Console Type
    // (dxSelectBox, required, fixed 3-item domain, BR-10),
    // Console Type (dxSelectBox, required, fixed 3-item domain, BR-10),
    // Commission Date/Retire Date (dxDateBox, dd MMM yyyy, optional) - the
    // real GCORPcx equivalent of prototype.html's own transparent-native-
    // date-input-overlay technique, which that file's own Dates rules
    // document as a static-HTML-only workaround for not having a real
    // DevExtreme component library available; the real Vue app does have
    // one, so this uses it directly, matching average-throughput.vue's own
    // real DateStart/DateEnd fields (dxDateBox, type: "date", displayFormat:
    // "dd MMM yyyy") rather than reproducing the prototype's overlay hack.
    //
    // `disabled: !this.isEditMode` on every field (06Sep2026 correction) -
    // read-only in Review, interactive only in Edit. gc-form.vue does not
    // gate a normal (allowUpdate: true) field's editability by baseView.mode
    // on its own (confirmed by reading it this session) - each screen must
    // do this explicitly, the same established pattern already used by
    // customer-agent.vue/grower-tab.vue/product-detail.vue
    // (`disabled: this.baseView.mode == "review"`).
    //
    // Tabbing sequence (07Sep2026, developer-requested - same mechanism
    // scheme-prices.vue's own Price Type/Variety/Item Code tabbing was built
    // on): this screen has no per-row conditional column disabling (every
    // Cell grid column is always editable in Edit mode, unlike scheme-
    // prices' PriceType-driven skip logic), so there is no walking/skip loop
    // needed here - only the two boundary transitions the developer asked
    // for. Console Name is auto-focused on entering Edit mode
    // (focusConsoleNameField, "baseView.mode" watch below); Console Name ->
    // Console Type -> Commission Date -> Retire Date is the existing plain
    // tabIndex 1-4 order already in place, needing no further code. Retire
    // Date's own onKeyDown (onRetireDateKeyDown) intercepts a forward Tab
    // out of it and moves focus into the Cell grid's row 0/column 0
    // (CellOrder) instead of falling through to native document tab order
    // (which has nowhere meaningful to go, since DxDataGrid cells aren't
    // part of that order at all until focused programmatically) - from
    // there, forward Tab/Shift+Tab inside the grid is DevExtreme's own
    // default cell navigation, unmodified. The grid's own onGridKeyDown
    // (see methods) handles only the reverse boundary - Shift+Tab out of
    // row 0/column 0 back to Retire Date - the mirror image of this.
    headerFormItems() {
      return [
        {
          itemType: "group",
          caption: "",
          colCount: 2,
          items: [
            {
              dataField: "ConsoleName",
              editorType: "dxTextBox",
              editorOptions: {
                maxLength: 50,
                tabIndex: 1,
                disabled: !this.isEditMode,
                onFocusIn: (e) => {
                  e.event.target.select();
                },
              },
              required: true,
              label: { text: "Console Name" },
              allowUpdate: true,
            },
            {
              dataField: "ConsoleType",
              editorType: "dxSelectBox",
              editorOptions: {
                dataSource: ["Sizer", "Production", "Inshell"],
                tabIndex: 2,
                disabled: !this.isEditMode,
              },
              required: true,
              label: { text: "Console Type" },
              allowUpdate: true,
            },
            {
              dataField: "CommissionDate",
              editorType: "dxDateBox",
              editorOptions: {
                type: "date",
                displayFormat: "dd MMM yyyy",
                tabIndex: 3,
                disabled: !this.isEditMode,
              },
              label: { text: "Commission Date" },
              allowUpdate: true,
            },
            {
              dataField: "RetireDate",
              editorType: "dxDateBox",
              editorOptions: {
                type: "date",
                displayFormat: "dd MMM yyyy",
                tabIndex: 4,
                disabled: !this.isEditMode,
                // Last header field - a forward Tab out of it jumps into
                // the Cell grid's row 0/column 0 instead of native tab
                // order (see this computed's own comment above).
                onKeyDown: this.onRetireDateKeyDown,
              },
              label: { text: "Retire Date" },
              allowUpdate: true,
            },
          ],
        },
      ];
    },

    // §6 Cell grid: 16 columns under 4 banded groups (DevExtreme's native
    // nested columns: [{ caption, columns: [...] }] syntax - see this file's
    // own Description for why a raw DxDataGrid, not GcDataGrid, is what
    // makes this possible). Domains match technical-design.md §7 BR-11's
    // corrected (real-data) values, not the Feature Vision's original
    // proposal. StopBits/DefaultCellSize have no fixed dropdown - see
    // SimarlooConsoleCells.cs's own remarks (FLAG-03/FLAG-09).
    //
    // `cssClass: this.isEditMode ? "edit-cell-...-field gc-editable-cell-color" : undefined`
    // on every editable column (06Sep2026 correction) - visually signals
    // Review as non-editable rather than looking identical to Edit, matching
    // scheme-prices.vue's own getEditableColumns convention exactly. The
    // actual lock is DxEditing's allow-updating below (bound to isEditMode) -
    // this class is styling only.
    cellGridColumns() {
      const textCss = this.isEditMode
        ? "edit-cell-textbox-field gc-editable-cell-color"
        : undefined;
      const numberCss = this.isEditMode
        ? "edit-cell-numberbox-field gc-editable-cell-color"
        : undefined;

      return [
        {
          caption: "Cell",
          columns: [
            {
              dataField: "CellOrder",
              caption: "Order",
              dataType: "number",
              alignment: "center",
              cssClass: numberCss,
              width: ColumnWidth.CellOrder.width,
              minWidth: ColumnWidth.CellOrder.minWidth,
            },
            {
              dataField: "CellType",
              caption: "Cell Type",
              cssClass: textCss,
              lookup: {
                dataSource: [
                  "",
                  "Sizer",
                  "Inshell Bin",
                  "Inshell Bag",
                  "Inshell Bin Tip",
                ],
              },
              width: ColumnWidth.CellType.width,
              minWidth: ColumnWidth.CellType.minWidth,
            },
            {
              dataField: "CellName",
              caption: "Cell Name",
              cssClass: textCss,
              editorOptions: { maxLength: 20 },
              width: ColumnWidth.Name.width,
              minWidth: ColumnWidth.Name.minWidth,
            },
            {
              dataField: "DefaultCellSize",
              caption: "Default Size",
              cssClass: textCss,
              editorOptions: { maxLength: 2 },
              width: ColumnWidth.DefaultCellSize.width,
              minWidth: ColumnWidth.DefaultCellSize.minWidth,
            },
          ],
        },
        {
          caption: "Serial (RS-232)",
          columns: [
            {
              dataField: "PortName",
              caption: "Port Name",
              cssClass: textCss,
              editorOptions: { maxLength: 10 },
              width: ColumnWidth.PortName.width,
              minWidth: ColumnWidth.PortName.minWidth,
            },
            {
              dataField: "Parity",
              caption: "Parity",
              cssClass: textCss,
              lookup: { dataSource: ["None", "Odd", "Even", "Mark", "Space"] },
              width: ColumnWidth.Parity.width,
              minWidth: ColumnWidth.Parity.minWidth,
            },
            {
              // Free numeric-styled text, not a fixed dropdown (FLAG-03
              // revised, BR-05) - physically NCHAR(10), every real row
              // stores the literal character "1".
              dataField: "StopBits",
              caption: "Stop Bits",
              cssClass: textCss,
              width: ColumnWidth.StopBits.width,
              minWidth: ColumnWidth.StopBits.minWidth,
            },
            {
              dataField: "DataBits",
              caption: "Data Bits",
              dataType: "number",
              cssClass: textCss,
              // 0 included (15Sep2026) - the real sentinel value Inshell
              // Bag/Inshell Bin Tip Cells store (non-communicating, no real
              // Data Bits setting at all); without it, DevExtreme's lookup
              // can't match the stored 0 to any option and renders blank/
              // mismatched instead of showing the real value. Still only
              // ever a sentinel there - validateBeforeSave/
              // SimarlooConsoleBusinessRulesValidator both still require a
              // real 7/8 for every communicating Cell Type.
              lookup: { dataSource: [0, 7, 8] },
              width: ColumnWidth.DataBits.width,
              minWidth: ColumnWidth.DataBits.minWidth,
            },
            {
              dataField: "BaudRate",
              caption: "Baud Rate",
              dataType: "number",
              cssClass: textCss,
              // 0 included (15Sep2026) - same sentinel reasoning as DataBits
              // above.
              lookup: { dataSource: [0, 2400, 9600, 19200, 38400, 57600, 115200] },
              width: ColumnWidth.BaudRate.width,
              minWidth: ColumnWidth.BaudRate.minWidth,
            },
          ],
        },
        {
          caption: "Scale Head (TCP/IP)",
          columns: [
            {
              dataField: "ScaleHeadName",
              caption: "Scale Head Name",
              cssClass: textCss,
              editorOptions: { maxLength: 50 },
              width: ColumnWidth.ScaleHeadName.width,
              minWidth: ColumnWidth.ScaleHeadName.minWidth,
            },
            {
              dataField: "IPAddress",
              caption: "IP Address",
              cssClass: textCss,
              editorOptions: { maxLength: 50 },
              width: ColumnWidth.IPAddress.width,
              minWidth: ColumnWidth.IPAddress.minWidth,
            },
            {
              dataField: "PortNumber",
              caption: "Port Number",
              dataType: "number",
              cssClass: numberCss,
              width: ColumnWidth.PortNumber.width,
              minWidth: ColumnWidth.PortNumber.minWidth,
            },
            {
              dataField: "Url",
              caption: "URL",
              cssClass: textCss,
              editorOptions: { maxLength: 255 },
              width: ColumnWidth.Url.width,
              minWidth: ColumnWidth.Url.minWidth,
            },
            {
              dataField: "UserName",
              caption: "User Name",
              cssClass: textCss,
              editorOptions: { maxLength: 100 },
              width: ColumnWidth.Username.width,
              minWidth: ColumnWidth.Username.minWidth,
            },
          ],
        },
        {
          caption: "Status",
          columns: [
            {
              dataField: "Enabled",
              caption: "Enabled",
              dataType: "boolean",
              width: ColumnWidth.CheckBoxColumn.width + 30,
              minWidth: ColumnWidth.CheckBoxColumn.minWidth + 30,
            },
            {
              dataField: "RememberCloseoutSettings",
              caption: "Remember Closeout",
              dataType: "boolean",
              width: ColumnWidth.GridCellCheckBox.width,
              minWidth: ColumnWidth.GridCellCheckBox.minWidth,
            },
          ],
        },
      ];
    },
  },

  async created() {
    this.showLoading(true);
    this.$nextTick(() => {
      this.showLoading(false);
      this.loaded = true;
    });
  },

  methods: {
    editCallBack() {
      this.rightGridLoaded = false;

      let width = undefined;

      const leftPanel = document.querySelectorAll(
        ".left-container.rightContentShow",
      );

      const rightPanels = document.querySelectorAll(
        ".split-container-child.right-container",
      );

      if (leftPanel[0].scrollWidth) {
        width = leftPanel[0].scrollWidth;
      } else {
        if (rightPanels[0].scrollWidth) {
          width = rightPanels[0].scrollWidth;
        }
      }

      if (width && rightPanels.length) {
        rightPanels[0].style.setProperty(
          "width",
          width - 12 + "px",
          "important",
        );
      }

      this.$nextTick(() => {
        setTimeout(() => {
          this.rightGridLoaded = true;

          this.$nextTick(() => {
            this.focusConsoleNameField();
          });
        }, 1000);
      });
    },
    backButtonFunction() {
      this.$router.push({ path: "/settings/intake" });
    },

    onActiveToggleChanged(e) {
      this.selectedRecord.Inactive = e.value ? "N" : "Y";
    },

    // 15Sep2026 correction (see History) - back to the standard, unconditional
    // "review" row click every other List+Detail screen uses. The 07Sep2026
    // canEditConsole-based routing this replaced (RW users straight to Edit,
    // Review-only users to Review) was built to work around
    // technical-design.md's then-stated "no Review/Edit toggle" spec, which
    // is itself what was wrong (confirmed with the PO, ADO #77470) - not the
    // standard row-click behaviour. Permission is enforced the standard way
    // instead: rightToolbarOptions.save (canEditConsole) gates whether the
    // Edit pencil even renders in Review, so a Review-only user simply never
    // sees it.
    onLeftRowClick(e) {
      if (!e.data) return;
      this.rightGridLoaded = false;
      this.onRowClicked(e, this.leftGridConfig.gridData.gridOptions.rowSelect);
    },

    // §5: AlmondcoSizerConsoleSummary, filtered to a single row's RecordId -
    // resolves the header (and, since #76661, the full audit block) on
    // baseView.id change, whether that's a row click or a direct/bookmarked
    // URL. No second raw-header fetch against api/SimarlooConsoles - see
    // this file's own Description.
    async loadSelectedRecord(recordId) {
      this.showLoading(true);
      try {
        const res = await AlmondcoSizerConsoleService.getSizerConsoleSummary(
          recordId,
        );
        const rows = (res.data && res.data.value) || [];
        const summary = rows[0] || {};
        if (summary.RecordId) {
          this.selectedRecord = summary;
          // Active toggle - same "Y"/"N" <-> boolean convention as
          // intake-variety-size.vue's own Inactive switch.
          this.$set(
            this.selectedRecord,
            "InactiveBoolean",
            this.selectedRecord.Inactive === "N",
          );
          await this.loadConsoleCells(summary.RecordId);
        } else {
          this.selectedRecord = {};
          this.cellRows = [];
        }
      } catch (err) {
        this.selectedRecord = {};
        this.cellRows = [];
      } finally {
        this.showLoading(false);

        const rightPanels = document.querySelectorAll(
          ".split-container-child.right-container",
        );

        if (rightPanels.length) {
          rightPanels[0].style.setProperty(
            "width",
            rightPanels[0].scrollWidth - 12 + "px",
            "important",
          );
        }

        this.$nextTick(() => {
          setTimeout(() => {
            this.rightGridLoaded = true;
          }, 10);
        });
      }
    },

    // §5: every Cell belonging to this Console, in CellOrder ascending
    // order (BR-04) - the detail panel's own Cell grid load.
    async loadConsoleCells(consoleRecordId) {
      try {
        const res = await AlmondcoSizerConsoleService.getConsoleCells(
          consoleRecordId,
        );
        this.cellRows = (res.data && res.data.value) || [];
      } catch (err) {
        this.cellRows = [];
      }
    },

    // §5 Business validation: ConsoleName/ConsoleType required (matching
    // each column's real NOT NULL DB constraint) - re-checked here even
    // though GcForm's own required:true already shows inline validation,
    // same defence-in-depth discipline as scheme-prices.vue's own
    // validateBeforeSave.
    //
    // 07Sep2026 (SD), Bug #77469: extended to a full bulk pre-Save check
    // covering every required Cell field, not just the header. Developer
    // direction (verbatim): "we cant add any inline validations, all the
    // validations need to perform before saving, as a bulk. and display
    // user friendly errors on that" - explicitly ruling out DevExtreme
    // column-level validationRules (which only give an inline per-cell
    // visual cue on blur and can't gate this screen's Save at all, since
    // onSaveClicked reads this.cellRows directly with no
    // grid.saveEditData()/hasEditData() call - see investigation.md,
    // Almondco - 2116 - Sizer Console Master). Every row/field problem is
    // collected in one pass and reported together, not one-at-a-time.
    //
    // Widened beyond CellOrder alone (the Bug's own literal scope) to every
    // [Required] Cell property in SaveModels/SimarlooConsole.cs -
    // CellOrder/CellName/DefaultCellSize/PortName/Parity/StopBits/DataBits/
    // BaudRate (confirmed by reading every [Required] annotation there;
    // CellType and RecordId are excluded - CellType has no [Required], and
    // RecordId is a system-managed existing-row reference, never user-
    // editable). This closes the gap the Bug's own ticket assumed was
    // already closed ("the same as every other required Cell field") -
    // confirmed during investigation that none of them actually were.
    // Developer confirmed this wider scope explicitly via AskUserQuestion.
    //
    // 15Sep2026 (SD), Bug reported (developer, real-data screenshot): Inshell
    // Bag/Inshell Bin Tip Cells were blocked from saving with "missing"
    // Port Name/Parity/Stop Bits - but real data (this project's own
    // prototype.html, PO-supplied 2026-08-25) shows these two Cell Types are
    // non-communicating destination Cells with NO port assignment at all
    // (blank Port Name/Parity/Stop Bits, 0 Data Bits/Baud Rate), unlike Sizer/
    // Inshell Bin/blank Cells, which are real sizing stations with a real
    // hardware port. These 5 fields are required only for a communicating
    // Cell now - see NON_COMMUNICATING_CELL_TYPES below, matching the
    // corresponding server-side relaxation in SimarlooConsole.cs/
    // SimarlooConsoleBusinessRulesValidator.cs.
    validateBeforeSave() {
      var self = this;
      var items = [];

      if (
        !this.selectedRecord.ConsoleName ||
        !this.selectedRecord.ConsoleName.trim()
      ) {
        items.push("Console Name is required.");
      }
      if (!this.selectedRecord.ConsoleType) {
        items.push("Console Type is required.");
      }

      // A blank/cleared DevExtreme grid cell arrives as null/undefined (or,
      // for a text editor, sometimes "") - never the numeric 0, which is a
      // legitimate value this check must not reject. Domain-value checks
      // (e.g. DataBits must be 7 or 8) are a separate concern, already
      // covered server-side by SimarlooConsoleBusinessRulesValidator - this
      // is presence only, matching the Bug's own "leaving it blank" scope.
      var isBlank = function (value) {
        return (
          value === null ||
          value === undefined ||
          (typeof value === "string" && value.trim() === "")
        );
      };
      var alwaysRequiredCellFields = ["CellOrder", "CellName", "DefaultCellSize"];
      // Communicating-Cell-only fields (15Sep2026) - a real sizing station
      // needs a real port/parity/stop-bits/data-bits/baud-rate; Inshell Bag
      // and Inshell Bin Tip never do (real data, PO-confirmed).
      var communicatingCellFields = [
        "PortName",
        "Parity",
        "StopBits",
        "DataBits",
        "BaudRate",
      ];
      var NON_COMMUNICATING_CELL_TYPES = ["Inshell Bag", "Inshell Bin Tip"];

      (this.cellRows || []).forEach(function (cell, index) {
        var requiredCellFields = NON_COMMUNICATING_CELL_TYPES.indexOf(cell.CellType) !== -1
          ? alwaysRequiredCellFields
          : alwaysRequiredCellFields.concat(communicatingCellFields);
        var missing = requiredCellFields.filter(function (field) {
          return isBlank(cell[field]);
        });
        if (missing.length > 0) {
          var labels = missing.map(function (field) {
            return self.friendlyFieldLabel(field);
          });
          items.push(
            "Row " + (index + 1) + " is missing: " + labels.join(", ") + ".",
          );
        }
      });

      if (items.length === 0) return null;
      if (items.length === 1) return items[0];
      return (
        "<ul>" +
        items
          .map(function (m) {
            return "<li>" + m + "</li>";
          })
          .join("") +
        "</ul>"
      );
    },

    // Maps a raw model-validation field name (e.g. "PortName", "ConsoleName")
    // to the same human label already shown on this screen's own header/grid
    // columns (technical-design.md §6), so a user-facing error message never
    // echoes a raw C# property name verbatim - 07Sep2026 (SD), Bug #77467:
    // QA's real complaint was that the raw ASP.NET Core wording itself
    // ("The PortName field is required.") wasn't user-friendly. Falls
    // back to a simple PascalCase -> "Title Case" split for anything not
    // explicitly listed, so an unmapped/future field still never renders
    // raw instead of silently doing nothing.
    friendlyFieldLabel(fieldName) {
      var labels = {
        ConsoleName: "Console Name",
        ConsoleType: "Console Type",
        CommissionDate: "Commission Date",
        RetireDate: "Retire Date",
        Inactive: "Inactive",
        CellOrder: "Cell Order",
        CellType: "Cell Type",
        CellName: "Cell Name",
        DefaultCellSize: "Default Size",
        PortName: "Port Name",
        Parity: "Parity",
        StopBits: "Stop Bits",
        DataBits: "Data Bits",
        BaudRate: "Baud Rate",
        ScaleHeadName: "Scale Head Name",
        Url: "URL",
        IPAddress: "IP Address",
        PortNumber: "Port Number",
        UserName: "User Name",
        RememberCloseoutSettings: "Remember Closeout Settings",
        Enabled: "Enabled",
        RecordId: "Record Id",
      };
      if (labels[fieldName]) return labels[fieldName];
      return fieldName
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
    },

    // Builds onSaveClicked's own $alert message from whatever the API
    // actually returned (06Sep2026, developer-reported: required-field
    // validation errors were coming back but not shown properly). Handles
    // three distinct shapes, in order:
    //   1. ASP.NET Core [ApiController]'s own automatic ModelState
    //      validation (RFC 9110 problem+json - Status/Title/Errors/traceId).
    //      Fires from SaveModels/SimarlooConsole.cs's [Required]/[MaxLength]
    //      annotations before the request ever reaches the controller
    //      action or SimarlooConsoleBusinessRulesValidator, so its shape
    //      (`Errors: { "Cells[0].Parity": ["The Parity field is required."] }`)
    //      is different from #2 below - this was the gap: it fell through
    //      to the generic message instead of showing the real field-level
    //      reasons.
    //
    //      07Sep2026 (SD), Bug #77467:
    //      CORRECTION (07Sep2026, same day): this branch was briefly changed
    //      to check data.Errors (capital E), on the theory that Program.cs's
    //      JsonSerializerOptions.PropertyNamingPolicy = null (no camelCase
    //      conversion) meant the real response serializes PascalCase. That
    //      theory was wrong and the developer confirmed it empirically
    //      against a real response: data.errors (lowercase) is correct.
    //      Reason - ASP.NET Core's built-in ValidationProblemDetails/
    //      ProblemDetails classes (Microsoft.AspNetCore.Mvc namespace) carry
    //      their own hardcoded [JsonPropertyName("errors")] (and "type"/
    //      "title"/"status"/"detail") attributes, RFC 9110-compliant and
    //      lowercase by design - an explicit JsonPropertyName always wins
    //      over an app-wide PropertyNamingPolicy, regardless of what
    //      Program.cs configures. Confirmed by reading
    //      GlobalExceptionHandlingMiddleware.cs (GcorpAPI/_Base/Middleware),
    //      which serializes a plain Microsoft.AspNetCore.Mvc.ProblemDetails
    //      object with JsonSerializer.Serialize(problem) - no custom options
    //      at all - and still needs data.detail/data.title lowercase (see #4
    //      below), consistent with the same hardcoded attributes. Reverted
    //      to data.errors here and to data.detail/data.title in #4 below.
    //      See investigation.md's Bug #77467 section, "Correction" paragraph,
    //      for the full write-up.
    //      Part 2 - QA's actual complaint:
    //      even with the field detail reaching the client, ASP.NET Core's
    //      own default wording ("The PortName field is required.", one
    //      sentence per field per row) is not user-friendly - raw,
    //      code-cased property names, and a wall of near-duplicate bullets
    //      for a single incomplete row. A [Required] violation (the only
    //      shape this screen's own SaveModels produce today) is now
    //      consolidated into one sentence per row/level - "Row 2 is missing:
    //      Parity, Port Name, Stop Bits." - using friendlyFieldLabel above
    //      instead of the raw property name. Any other message shape (kept
    //      for forward-compatibility - e.g. a MaxLength violation, now
    //      reachable too since Bug #77465 added a real MaxLength(50) to
    //      ConsoleName) still renders individually, with the raw field-name
    //      token swapped for its friendly label wherever it appears, so an
    //      unanticipated shape still never echoes a raw property name.
    //      See investigation.md (Almondco - 2116 - Sizer Console Master)
    //      for the full write-up of both parts.
    //   2. SimarlooConsoleBusinessRulesValidator's own BadRequest array of
    //      ValidationResult-shaped objects (#76659) - the pre-existing
    //      handling, unchanged, matching every sibling 2116 screen's own
    //      convention (e.g. grower-pool-splits.vue's onSaveClicked).
    //   3. A plain string body.
    //   4. A ProblemDetails-shaped object with no Errors dictionary - added
    //      07Sep2026 (SD), Bug #77467 Part 3 (developer-clarified: "don't we
    //      know what that error comes from... something is not there to
    //      make the save... that should be the error message"). This is
    //      exactly what GlobalExceptionHandlingMiddleware.cs
    //      (GcorpAPI/_Base/Middleware) returns for ANY unhandled server
    //      exception during Save: { Status: 500, Title: "Server Error
    //      Occurred", Detail: "An internal server error occurred. Trace ID
    //      <id>. Additional information: <exception message>." } - a real,
    //      specific reason (plus a Trace ID for support) that was already
    //      being sent to the browser and simply had no matching branch here,
    //      so it fell through to the fully generic fallback and was
    //      discarded exactly like Parts 1/2's own gap.
    // Every sibling 2116 screen's own Save error handling has the same #1
    // gap (checked this session) - fixing it there is out of scope here.
    buildSaveErrorMessage(data) {
      if (
        data &&
        typeof data === "object" &&
        data.errors &&
        typeof data.errors === "object"
      ) {
        var self = this;
        // Grouped by Cell row ("row0", "row1", ...) or "top" for a
        // Console-header field with no Cells[N]. prefix.
        var groups = {};

        Object.keys(data.errors).forEach(function (field) {
          var fieldMessages = data.errors[field];
          if (!Array.isArray(fieldMessages)) return;

          var cellMatch = field.match(/^Cells\[(\d+)\]\.(.+)$/);
          var rawFieldName = cellMatch ? cellMatch[2] : field;
          var rowIndex = cellMatch ? parseInt(cellMatch[1], 10) : null;
          var groupKey = cellMatch ? "row" + rowIndex : "top";
          var label = self.friendlyFieldLabel(rawFieldName);

          if (!groups[groupKey]) {
            groups[groupKey] = { rowIndex: rowIndex, missing: [], other: [] };
          }

          fieldMessages.forEach(function (m) {
            var isRequiredMessage =
              typeof m === "string" &&
              new RegExp(
                "^The " + rawFieldName + " field is required\\.$",
                "i",
              ).test(m);
            if (isRequiredMessage) {
              groups[groupKey].missing.push(label);
            } else {
              var friendlyMessage =
                typeof m === "string" ? m.split(rawFieldName).join(label) : m;
              groups[groupKey].other.push(friendlyMessage);
            }
          });
        });

        var items = [];
        Object.keys(groups).forEach(function (groupKey) {
          var group = groups[groupKey];
          var rowPrefix =
            group.rowIndex !== null ? "Row " + (group.rowIndex + 1) + " " : "";
          if (group.missing.length > 0) {
            items.push(rowPrefix + "is missing: " + group.missing.join(", ") + ".");
          }
          group.other.forEach(function (m) {
            items.push(
              (group.rowIndex !== null ? "Row " + (group.rowIndex + 1) + ": " : "") +
                m,
            );
          });
        });

        if (items.length > 0) {
          return (
            "<ul>" +
            items
              .map(function (m) {
                return "<li>" + m + "</li>";
              })
              .join("") +
            "</ul>"
          );
        }
      } else if (Array.isArray(data) && data.length > 0) {
        return data
          .map(function (e) {
            return e.ErrorMessage || e;
          })
          .join(" ");
      } else if (typeof data === "string" && data.length > 0) {
        return data;
      } else if (data && typeof data === "object" && (data.detail || data.title)) {
        // 07Sep2026 (SD), Bug #77467 Part 3 (developer-clarified: the failure
        // reason is usually actually knowable, so it should be shown rather
        // than defaulting to a fully generic message). GlobalExceptionHandlingMiddleware.cs
        // (GcorpAPI/_Base/Middleware) catches any unhandled server exception
        // during Save and already returns a real, specific ProblemDetails
        // with no errors dictionary (it's not a validation failure) - just
        // Title: "Server Error Occurred" and Detail: "An internal server
        // error occurred. Trace ID <id>. Additional information: <message>."
        // Both serialize lowercase (detail/title) - same hardcoded
        // [JsonPropertyName] attributes on Microsoft.AspNetCore.Mvc.
        // ProblemDetails as data.errors above, confirmed by reading
        // GlobalExceptionHandlingMiddleware.cs itself (plain
        // JsonSerializer.Serialize(problem), no custom casing options).
        // That Detail text was already being sent to the browser (visible in
        // the Network tab regardless of this branch) - it just had no
        // matching shape here, so it fell through to the fully generic
        // fallback below and was discarded. detail is preferred (it carries
        // the Trace ID and the actual exception message); title alone covers
        // the rare case a caller sends ProblemDetails fields without Detail.
        return data.detail || data.title;
      }
      // True last resort: nothing recognizable came back at all (e.g. a
      // network failure/timeout with no response body) - the one case where
      // there is genuinely no further reason to report. Still gives the
      // user a next step rather than a dead end (07Sep2026, Bug #77467).
      return "An error occurred while saving the Sizer Console. Please try again, or contact support if the problem continues.";
    },

    async onSaveClicked() {
      const activeElement = document.activeElement;
      if (activeElement) activeElement.blur();
      await this.$nextTick();

      const blockingMessage = this.validateBeforeSave();
      if (blockingMessage) {
        await this.$alert(blockingMessage, "Unable to Save");
        return;
      }

      this.showLoading(true);
      try {
        // Full resupply (technical-design.md §9 Story 5) - every save
        // re-sends the complete header plus every Cell row currently in the
        // grid, never a partial/incremental update (matches
        // SimarlooConsoleBusiness.Save's own expectation, #76659).
        const payload = {
          RecordId: this.selectedRecord.RecordId,
          ConsoleName: this.selectedRecord.ConsoleName,
          ConsoleType: this.selectedRecord.ConsoleType,
          CommissionDate: this.selectedRecord.CommissionDate || null,
          RetireDate: this.selectedRecord.RetireDate || null,
          Inactive: this.selectedRecord.Inactive,
          Cells: this.cellRows.map((c) => ({
            RecordId: c.RecordId,
            CellOrder: c.CellOrder,
            CellType: c.CellType || "",
            CellName: c.CellName,
            DefaultCellSize: c.DefaultCellSize,
            PortName: c.PortName,
            Parity: c.Parity,
            StopBits: c.StopBits,
            DataBits: c.DataBits,
            BaudRate: c.BaudRate,
            ScaleHeadName: c.ScaleHeadName || null,
            IPAddress: c.IPAddress,
            PortNumber: c.PortNumber,
            Url: c.Url || null,
            UserName: c.UserName || null,
            RememberCloseoutSettings: c.RememberCloseoutSettings,
            Enabled: c.Enabled,
          })),
        };

        await AlmondcoSizerConsoleService.saveSizerConsole(payload);

        this.setIgnoreRouteChangeConfirmation();
        // Stays on the current /edit/:id route rather than navigating to
        // /review/:id post-save (matches scheme-prices.vue's own
        // onSaveClicked, which does the same) - just refresh in place: the
        // header (fresh ChangedBy/DateChanged from the shim SP), the Cell
        // grid, and the List (in case ConsoleName/ConsoleType/Status
        // changed, all shown there).
        await this.loadSelectedRecord(this.baseView.id);
        if (this.$refs.leftGrid) {
          this.$refs.leftGrid.gridRefresh(true);
        }

        this.$nextTick(() => {
          this.$router.push({ path: "/sizer-console" });
        });
      } catch (err) {
        var data =
          (err && err.data) || (err && err.response && err.response.data);
        await this.$alert(this.buildSaveErrorMessage(data), "Unable to Save");
      } finally {
        this.showLoading(false);
        this.setRightGridHeight();
      }
    },

    onGridContentReady(e) {
      if (e) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.setRightGridHeight();
          }, 500);
        });
      }
    },

    // Tabbing sequence (07Sep2026, developer-requested) - see
    // headerFormItems' own comment for the full design. Only these two
    // boundary transitions need custom handling; everything else (native
    // tabIndex order across the header fields, native cell-to-cell Tab once
    // inside the grid) is unmodified default behaviour.
    //
    // Forward Tab out of Retire Date (the last header field) -> Cell grid
    // row 0/column 0 (CellOrder), instead of falling through to native
    // document tab order, which has nowhere meaningful to go (DxDataGrid
    // cells aren't part of that order at all until focused
    // programmatically).
    onRetireDateKeyDown(e) {
      if (!this.isEditMode) return;
      const keyEvent = e && e.event;
      if (!keyEvent || keyEvent.key !== "Tab" || keyEvent.shiftKey) return;

      const grid = this.$refs.rightGrid;
      if (!grid || !grid.instance) return;

      keyEvent.preventDefault();
      const activeElement = document.activeElement;
      if (activeElement) activeElement.blur();
      this.$nextTick(() => {
        this.moveFocusToCell(grid.instance, 0, 0);
      });
    },

    // Mirror image of onRetireDateKeyDown - only intercepts Shift+Tab out of
    // the grid's very first cell (row 0/column 0); every other Tab/
    // Shift+Tab inside the grid falls through to DevExtreme's own default
    // cell navigation untouched. No per-row skip logic is needed here,
    // unlike scheme-prices.vue's own onGridKeyDown - every Cell grid column
    // in this screen is always editable in Edit mode (no PriceType-style
    // conditional disabling), so there is nothing to walk past.
    onGridKeyDown(e) {
      if (!this.isEditMode) return;
      const keyEvent = e && e.event;
      if (!keyEvent || keyEvent.key !== "Tab" || !keyEvent.shiftKey) return;

      const grid = e.component;
      const row = grid.option("focusedRowIndex");
      const col = grid.option("focusedColumnIndex");
      if (row !== 0 || col !== 0) return;

      keyEvent.preventDefault();
      const activeElement = document.activeElement;
      if (activeElement) activeElement.blur();
      this.$nextTick(() => {
        const editor =
          this.$refs.headerForm &&
          this.$refs.headerForm.getEditorField("RetireDate");
        if (editor) editor.focus();
      });
    },

    // Shared by both boundary transitions above - explicitly setting
    // focusedRowIndex/focusedColumnIndex *and* calling editCell together is
    // required, not optional: editCell() alone does not reliably keep those
    // two options in sync with where focus actually moved to, the same bug
    // scheme-prices.vue's own identical helper was built to fix (see that
    // file's own History, 2026-09-03) - reused here rather than
    // re-discovering it.
    moveFocusToCell(grid, rowIndex, columnIndex) {
      grid.option("focusedRowIndex", rowIndex);
      grid.option("focusedColumnIndex", columnIndex);
      grid.editCell(rowIndex, columnIndex);
    },

    // Auto-focuses Console Name every time this screen enters Edit mode
    // (developer-requested, 07Sep2026) - whether that's the toolbar's Edit
    // button from Review, or a direct/bookmarked /edit/:id visit (see the
    // "baseView.mode" watch below). Retried on a short delay (matches this
    // file's own onGridContentReady/setRightGridHeight convention) since
    // GcForm's own underlying DxForm may not have mounted yet the instant
    // baseView.mode flips - getEditorField returns false until it has.
    focusConsoleNameField() {
      this.$nextTick(() => {
        setTimeout(() => {
          const editor =
            this.$refs.headerForm &&
            this.$refs.headerForm.getEditorField("ConsoleName");
          if (editor) editor.focus();
        }, 300);
      });
    },

    // Verified against the real gc-split-view.vue markup this task
    // (".split-container-child.right-container") - see this file's own
    // Description for the sibling scheme-prices.vue selector bug this
    // deliberately does not repeat.
    setRightGridHeight() {
      setTimeout(() => {
        const rightPanel = document.querySelector(
          ".split-container-child.right-container",
        );

        if (!this.showRightPanel || !rightPanel) return;

        const formHeightEl = document.querySelector(".selected-record-info");
        const formHeight = formHeightEl ? formHeightEl.clientHeight : 0;

        this.rightGridHeight =
          rightPanel.clientHeight - formHeight - 10 - 40 - 12 - 12 - 18;

        this.$nextTick(() => {
          const grid = this.$refs.rightGrid;
          if (grid && grid.instance) {
            grid.instance.updateDimensions();
          }
        });
      }, 10);
    },
  },

  watch: {
    // Drives both the row-click flow and a direct/bookmarked URL identically -
    // baseView.id is route-derived (RouteMixin), so this fires either way.
    "baseView.id": {
      immediate: true,
      handler(routeId) {
        if (routeId) {
          this.loadSelectedRecord(routeId);
        } else {
          this.selectedRecord = {};
          this.cellRows = [];
        }
        this.setRightGridHeight();
      },
    },

    // Tabbing sequence (07Sep2026, developer-requested): Console Name is
    // auto-focused every time this screen enters Edit mode - the toolbar's
    // Edit button from Review, or a direct/bookmarked /edit/:id visit
    // (immediate: true covers the latter).
    "baseView.mode": {
      immediate: true,
      handler(mode) {
        if (mode === "edit") {
          this.focusConsoleNameField();
        }
      },
    },
  },
};
</script>

<style lang="scss">
.sizer-console {
  .split-view-right-grid {
    flex: 1;
    overflow: auto;
    max-width: 100%;
  }

  .split-grid-footer {
    display: flex;
    align-items: center;
    padding: 8px 0;
    font-size: 12px;
    color: var(--text-tertiary, #475467);
  }

  .selected-record-info {
    padding: 12px 0px 12px 0px;

    .dx-form {
      background-color: white;
      padding: 12px;
    }

    // #76649: spacing above the create-mode identity form's inline START
    // button (identityFormItems), matching the vertical rhythm of the
    // Season/Scheme fields it sits below.
    .scheme-price-start-group {
      margin-top: 8px;
    }
  }

  .split-view-right-grid {
    padding: unset !important;
  }

  .right-panel-container {
    width: inherit !important;

    .split-view-right-grid {
      width: inherit !important;

      .dx-widget.dx-card.wide-card {
        width: inherit !important;
      }
    }
  }
}
</style>
